<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { refDebounced } from '@vueuse/core'
import type { ChatAttachment, ChatEmbed, ChatMessage, CreateMessagePayload } from '@/services/chat/chatHttp.service'
import { chatApi } from '@/services/api'
import BButton from '@/components/common/bButton.vue'
import MentionArea from '@/components/common/MentionArea/MentionArea.vue'
import type { MentionablePerson } from '@/components/common/MentionArea/suggestion'
import GifPicker from '@/components/messaging/GifPicker.vue'
import EmbedPicker from '@/components/messaging/EmbedPicker.vue'
import MessageEmbed from '@/components/messaging/MessageEmbed.vue'
import MaterialSymbolsSendRounded from '~icons/material-symbols/send-rounded'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'
import MaterialSymbolsGifBoxOutlineRounded from '~icons/material-symbols/gif-box-outline-rounded'
import MaterialSymbolsLinkRounded from '~icons/material-symbols/link-rounded'
import MaterialSymbolsAttachFileRounded from '~icons/material-symbols/attach-file-rounded'
import MaterialSymbolsBarChartRounded from '~icons/material-symbols/bar-chart-rounded'
import { displayLocale } from '@/services/application/displayLocale'

const { t } = useI18n()

const props = defineProps<{
  disabled?: boolean
  sending?: boolean
  conversationId?: string | null
  replyTo?: ChatMessage | null
  editingMessage?: ChatMessage | null
}>()

const emits = defineEmits<{
  (e: 'send', payload: CreateMessagePayload): void
  (e: 'save-edit', payload: { messageId: string; body: string }): void
  (e: 'cancel-reply'): void
  (e: 'cancel-edit'): void
}>()

const MAX_FILE_BYTES = 50 * 1024 * 1024
const DEFAULT_EXPIRES_DAYS = 30

const draft = ref('')
const mentions = ref<any[]>([])
const pendingEmbeds = ref<ChatEmbed[]>([])
const pendingAttachments = ref<ChatAttachment[]>([])
const showGifPicker = ref(false)
const showEmbedPicker = ref(false)
const showPollComposer = ref(false)
const showFileOptions = ref(false)
const unfurlPreview = ref<ChatEmbed | null>(null)
const dismissedUnfurlKey = ref<string | null>(null)
const mentionables = ref<MentionablePerson[]>([])
const uploading = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)
const expiresInDays = ref(DEFAULT_EXPIRES_DAYS)

const pollQuestion = ref('')
const pollOptions = ref(['', ''])

const loadMentionables = async (conversationId: string) => {
  try {
    const detail = await chatApi.getConversation(conversationId).promise
    mentionables.value = (detail.members || []).map(member => ({
      _id: String(member.resourceId),
      firstname: member.firstname,
      lastname: member.lastname,
      picture: member.picture || null,
      display: `${member.firstname || ''} ${member.lastname || ''}`.trim(),
    }))
  }
  catch (err) {
    console.error('Failed to load mentionables', err)
    mentionables.value = []
  }
}

watch(() => props.conversationId, (id) => {
  mentionables.value = []
  pendingAttachments.value = []
  showPollComposer.value = false
  showFileOptions.value = false
  if (id) void loadMentionables(id)
}, { immediate: true })

watch(() => props.editingMessage, (message) => {
  if (message) {
    draft.value = message.body || ''
    showPollComposer.value = false
    showFileOptions.value = false
  }
})

const modeLabel = computed(() => {
  if (props.editingMessage) return t('modules.chat.editingMessage')
  if (props.replyTo) {
    const s = props.replyTo.sender
    const name = s ? `${s.firstname || ''} ${s.lastname || ''}`.trim() : ''
    return t('modules.chat.replyingTo', { name: name || t('modules.chat.replyToMessage') })
  }
  return null
})

const previewBody = computed(() => {
  if (props.editingMessage) return props.editingMessage.body
  if (props.replyTo) return props.replyTo.deletedAt ? t('modules.chat.messageDeleted') : props.replyTo.body
  return ''
})

const plainDraft = computed(() => draft.value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim())

const canSend = computed(() => {
  if (showPollComposer.value) {
    const options = pollOptions.value.map(o => o.trim()).filter(Boolean)
    return !!pollQuestion.value.trim() && options.length >= 2
  }
  return !!plainDraft.value || pendingEmbeds.value.length > 0 || pendingAttachments.value.length > 0
})

const expirationPreviewLabel = computed(() => {
  const days = Math.min(30, Math.max(1, Number(expiresInDays.value) || DEFAULT_EXPIRES_DAYS))
  const date = new Date(Date.now() + days * 24 * 60 * 60 * 1000)
  return new Intl.DateTimeFormat(displayLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
})

// Auto-unfurl pasted URLs
const debouncedDraft = refDebounced(draft, 500)
watch(debouncedDraft, async (value) => {
  if (props.editingMessage || showPollComposer.value) return
  const match = String(value).match(/https?:\/\/[^\s<"']+|\/(?:hr|recruitment|project|evaluation)\/[^\s<"']+/i)
  if (!match) {
    unfurlPreview.value = null
    dismissedUnfurlKey.value = null
    return
  }
  const urlKey = match[0]
  if (dismissedUnfurlKey.value === urlKey) {
    unfurlPreview.value = null
    return
  }
  try {
    const response = await chatApi.unfurl({ url: urlKey }).promise
    unfurlPreview.value = response.embed
  }
  catch {
    unfurlPreview.value = null
  }
})

const resetComposer = () => {
  draft.value = ''
  mentions.value = []
  pendingEmbeds.value = []
  pendingAttachments.value = []
  unfurlPreview.value = null
  dismissedUnfurlKey.value = null
  showEmbedPicker.value = false
  showGifPicker.value = false
  showPollComposer.value = false
  showFileOptions.value = false
  pollQuestion.value = ''
  pollOptions.value = ['', '']
}

const submit = () => {
  if (props.disabled || props.sending || uploading.value) return

  if (props.editingMessage) {
    const value = draft.value.trim()
    if (!value) return
    emits('save-edit', { messageId: props.editingMessage._id, body: value })
    draft.value = ''
    mentions.value = []
    return
  }

  if (!canSend.value) return

  if (showPollComposer.value) {
    const options = pollOptions.value.map(o => o.trim()).filter(Boolean)
    emits('send', {
      body: '',
      type: 'poll',
      poll: {
        question: pollQuestion.value.trim(),
        options,
      },
      replyToMessageId: props.replyTo?._id || null,
    })
    resetComposer()
    return
  }

  const embeds = [...pendingEmbeds.value]
  if (unfurlPreview.value && !embeds.some(e => e.entityId === unfurlPreview.value?.entityId)) {
    embeds.push(unfurlPreview.value)
  }

  const mentionIds = [...new Set(mentions.value.map(m => m._id).filter(Boolean))]
  emits('send', {
    body: draft.value,
    type: 'text',
    mentionIds,
    replyToMessageId: props.replyTo?._id || null,
    embeds,
    attachmentIds: pendingAttachments.value.map(a => a._id),
  })
  resetComposer()
}

const sendGif = (gif: { provider: string; id: string; url: string; previewUrl?: string }) => {
  emits('send', {
    body: '',
    type: 'gif',
    gif,
    replyToMessageId: props.replyTo?._id || null,
  })
  showGifPicker.value = false
}

const addEmbed = (embed: ChatEmbed) => {
  if (!pendingEmbeds.value.some(e => e.entityId === embed.entityId && e.type === embed.type)) {
    pendingEmbeds.value.push(embed)
  }
  showEmbedPicker.value = false
}

const removePendingEmbed = (embed: ChatEmbed) => {
  pendingEmbeds.value = pendingEmbeds.value.filter(
    e => !(e.entityId === embed.entityId && e.type === embed.type),
  )
}

const removeUnfurlPreview = () => {
  const match = String(draft.value).match(/https?:\/\/[^\s<"']+|\/(?:hr|recruitment|project|evaluation)\/[^\s<"']+/i)
  dismissedUnfurlKey.value = match?.[0] || null
  unfurlPreview.value = null
}

const removePendingAttachment = (attachment: ChatAttachment) => {
  pendingAttachments.value = pendingAttachments.value.filter(a => a._id !== attachment._id)
}

const openFilePicker = () => {
  showFileOptions.value = true
  showGifPicker.value = false
  showEmbedPicker.value = false
  showPollComposer.value = false
  fileInputRef.value?.click()
}

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file || !props.conversationId) return

  if (file.size > MAX_FILE_BYTES) {
    window.alert(t('modules.chat.file.tooLarge'))
    return
  }

  uploading.value = true
  try {
    const days = Math.min(30, Math.max(1, Number(expiresInDays.value) || DEFAULT_EXPIRES_DAYS))
    const attachment = await chatApi.uploadFile(props.conversationId, file, {
      expiresInDays: days,
      fileDescription: file.name,
    }).promise
    pendingAttachments.value.push(attachment)
    showFileOptions.value = true
  }
  catch (err) {
    console.error('Failed to upload chat file', err)
    window.alert(t('modules.chat.file.uploadError'))
  }
  finally {
    uploading.value = false
  }
}

const addPollOption = () => {
  if (pollOptions.value.length >= 10) return
  pollOptions.value.push('')
}

const removePollOption = (index: number) => {
  if (pollOptions.value.length <= 2) return
  pollOptions.value.splice(index, 1)
}

const togglePollComposer = () => {
  showPollComposer.value = !showPollComposer.value
  showGifPicker.value = false
  showEmbedPicker.value = false
  showFileOptions.value = false
}

const cancelBanner = () => {
  if (props.editingMessage) emits('cancel-edit')
  else emits('cancel-reply')
}
</script>

<template>
  <div
    class="border-t dark:border-white/10 bg-transparent relative"
    data-cy="chat-composer"
  >
    <div
      v-if="modeLabel"
      class="px-3 pt-2 flex items-start gap-2"
      data-cy="chat-composer-banner"
    >
      <div class="flex-1 min-w-0 rounded-lg bg-primary-500/10 px-2 py-1.5 text-xs">
        <div class="font-medium truncate">
          {{ modeLabel }}
        </div>
        <div class="opacity-70 truncate">
          {{ previewBody }}
        </div>
      </div>
      <button
        type="button"
        class="p-1 rounded-sm bg-hover shrink-0"
        data-cy="chat-composer-cancel-banner"
        @click="cancelBanner"
      >
        <MaterialSymbolsCloseRounded class="w-4 h-4" />
      </button>
    </div>

    <div
      v-if="pendingEmbeds.length || unfurlPreview"
      class="px-2.5 pt-2 space-y-1"
    >
      <MessageEmbed
        v-for="embed in pendingEmbeds"
        :key="`${embed.type}-${embed.entityId}`"
        :embed="embed"
        removable
        @remove="removePendingEmbed(embed)"
      />
      <MessageEmbed
        v-if="unfurlPreview && !pendingEmbeds.some(e => e.entityId === unfurlPreview?.entityId)"
        :embed="unfurlPreview"
        removable
        @remove="removeUnfurlPreview"
      />
    </div>

    <div
      v-if="pendingAttachments.length || showFileOptions"
      class="px-2.5 pt-2 space-y-1.5"
    >
      <div class="rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-xs space-y-1.5">
        <div class="font-semibold text-amber-900 dark:text-amber-100">
          {{ t('modules.chat.file.expiryTitle') }}
        </div>
        <p class="opacity-90 leading-snug">
          {{ t('modules.chat.file.expiryHelp', { maxDays: 30 }) }}
        </p>
        <label class="flex items-center gap-2 flex-wrap">
          <span>{{ t('modules.chat.file.expiresInDays') }}</span>
          <input
            v-model.number="expiresInDays"
            type="number"
            min="1"
            max="30"
            data-cy="chat-file-expires-days"
            class="w-16 rounded-sm border border-black/15 dark:border-white/20 bg-white/80 dark:bg-black/20 px-1.5 py-0.5"
          >
          <span class="opacity-80">→ {{ expirationPreviewLabel }}</span>
        </label>
      </div>
      <div
        v-for="attachment in pendingAttachments"
        :key="attachment._id"
        class="flex items-center gap-2 rounded-lg border border-black/10 dark:border-white/15 bg-white/60 dark:bg-white/5 px-2 py-1.5 text-xs"
      >
        <div class="min-w-0 flex-1">
          <div class="font-medium truncate">
            {{ attachment.fileDescription || attachment.originalFileName }}
          </div>
          <div class="opacity-70">
            {{ t('modules.chat.file.pendingExpiry', {
              date: new Intl.DateTimeFormat(displayLocale, { dateStyle: 'medium', timeStyle: 'short' }).format(
                attachment.expiresAt instanceof Date ? attachment.expiresAt : new Date(attachment.expiresAt),
              ),
            }) }}
          </div>
        </div>
        <button
          type="button"
          class="p-1 rounded-sm bg-hover shrink-0"
          @click="removePendingAttachment(attachment)"
        >
          <MaterialSymbolsCloseRounded class="w-4 h-4" />
        </button>
      </div>
    </div>

    <div
      v-if="showPollComposer"
      class="px-2.5 pt-2 space-y-2"
    >
      <div
        class="rounded-xl border border-black/10 dark:border-white/15 bg-white/70 dark:bg-white/5 p-2.5 space-y-2"
        data-cy="chat-poll-composer"
      >
        <div class="flex items-center justify-between gap-2">
          <div class="text-sm font-medium">
            {{ t('modules.chat.poll.title') }}
          </div>
          <button
            type="button"
            class="p-1 rounded-sm bg-hover"
            data-cy="chat-poll-composer-close"
            @click="showPollComposer = false"
          >
            <MaterialSymbolsCloseRounded class="w-4 h-4" />
          </button>
        </div>
        <input
          v-model="pollQuestion"
          type="text"
          data-cy="chat-poll-question"
          class="w-full rounded-lg border border-black/10 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm"
          :placeholder="t('modules.chat.poll.questionPlaceholder')"
        >
        <div
          v-for="(_, index) in pollOptions"
          :key="index"
          class="flex items-center gap-1.5"
        >
          <input
            v-model="pollOptions[index]"
            type="text"
            :data-cy="`chat-poll-option-${index}`"
            class="flex-1 rounded-lg border border-black/10 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm"
            :placeholder="t('modules.chat.poll.optionPlaceholder', { n: index + 1 })"
          >
          <button
            v-if="pollOptions.length > 2"
            type="button"
            class="p-1 rounded-sm bg-hover"
            :data-cy="`chat-poll-option-remove-${index}`"
            @click="removePollOption(index)"
          >
            <MaterialSymbolsCloseRounded class="w-4 h-4" />
          </button>
        </div>
        <button
          v-if="pollOptions.length < 10"
          type="button"
          class="text-xs text-primary-500 hover:underline"
          data-cy="chat-poll-add-option"
          @click="addPollOption"
        >
          {{ t('modules.chat.poll.addOption') }}
        </button>
      </div>
    </div>

    <div
      v-if="showGifPicker"
      class="absolute bottom-full left-2 right-2 mb-2 z-30"
    >
      <GifPicker
        @select="sendGif"
        @close="showGifPicker = false"
      />
    </div>
    <div
      v-if="showEmbedPicker"
      class="absolute bottom-full left-2 right-2 mb-2 z-30"
    >
      <EmbedPicker
        @select="addEmbed"
        @close="showEmbedPicker = false"
      />
    </div>

    <input
      ref="fileInputRef"
      type="file"
      class="hidden"
      data-cy="chat-file-input"
      @change="onFileSelected"
    >

    <div class="px-2.5 py-2 flex gap-1.5 items-end">
      <div class="flex-1 min-w-0 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 shadow-xs flex flex-col">
        <div class="flex items-end gap-0.5 pl-1 pr-2 py-1 min-h-11">
          <div class="flex shrink-0 self-end pb-0.5">
            <button
              type="button"
              class="p-1.5 rounded-lg bg-hover disabled:opacity-40"
              data-cy="chat-gif-button"
              :title="t('modules.chat.gif.title')"
              :disabled="!!editingMessage || showPollComposer"
              @click="showGifPicker = !showGifPicker; showEmbedPicker = false; showPollComposer = false"
            >
              <MaterialSymbolsGifBoxOutlineRounded class="w-5 h-5" />
            </button>
            <button
              type="button"
              class="p-1.5 rounded-lg bg-hover disabled:opacity-40"
              data-cy="chat-embed-button"
              :title="t('modules.chat.embed.title')"
              :disabled="!!editingMessage || showPollComposer"
              @click="showEmbedPicker = !showEmbedPicker; showGifPicker = false; showPollComposer = false"
            >
              <MaterialSymbolsLinkRounded class="w-5 h-5" />
            </button>
            <button
              type="button"
              class="p-1.5 rounded-lg bg-hover disabled:opacity-40"
              data-cy="chat-attach-button"
              :title="t('modules.chat.file.attach')"
              :disabled="!!editingMessage || showPollComposer || uploading || !conversationId"
              @click="openFilePicker"
            >
              <MaterialSymbolsAttachFileRounded class="w-5 h-5" />
            </button>
            <button
              type="button"
              class="p-1.5 rounded-lg bg-hover disabled:opacity-40"
              data-cy="chat-poll-button"
              :title="t('modules.chat.poll.title')"
              :disabled="!!editingMessage"
              @click="togglePollComposer"
            >
              <MaterialSymbolsBarChartRounded class="w-5 h-5" />
            </button>
          </div>
          <MentionArea
            v-if="!showPollComposer"
            v-model="draft"
            v-model:mentions="mentions"
            bare
            data-cy="chat-composer-input"
            class="flex-1 min-w-0 max-h-28 overflow-y-auto py-1.5"
            :placeholder="t('modules.chat.messagePlaceholderShort')"
            :mentionables="mentionables"
            @submit="submit"
          />
          <div
            v-else
            class="flex-1 min-w-0 py-1.5 text-xs opacity-60"
          >
            {{ t('modules.chat.poll.composerHint') }}
          </div>
        </div>
      </div>
      <BButton
        bg="primary"
        size="small"
        data-cy="chat-send-button"
        :disabled="disabled || sending || uploading || !canSend"
        :title="t('modules.chat.send')"
        @click="submit"
      >
        <MaterialSymbolsSendRounded class="w-5 h-5" />
      </BButton>
    </div>
  </div>
</template>
